import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api.config.jsx';
import { hashPassword } from '../utils/passwordUtils.jsx';
import { trackEvent, EVENT_TYPE_PAGE } from '../utils/trackEvent.jsx';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";

const PartnerSignIn = () => {
  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
    console.log('Partner signin page loaded');
  }, []);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const handleChange = (e) => {
    setError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const saltRequestUrl = `${getApiUrl()}/auth/auth/salt`;
      
      const saltRequestBody = JSON.stringify({
        email: formData.email
      });
      
      const saltResponse = await fetch(saltRequestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: saltRequestBody
      });

      if (!saltResponse.ok) {
        const errorText = await saltResponse.text();
        console.error('Salt response error text:', errorText);
        throw new Error(`Salt request failed: ${saltResponse.status} - ${errorText}`);
      }

      const saltResponseData = await saltResponse.json();
      
      // Handle both response structures: {salt: "..."} and {data: {salt: "..."}}
      let salt;
      if (saltResponseData && saltResponseData.salt) {
        // Direct salt structure: {salt: "..."}
        salt = saltResponseData.salt;
      } else if (saltResponseData && saltResponseData.data && saltResponseData.data.salt) {
        // Nested salt structure: {data: {salt: "..."}}
        salt = saltResponseData.data.salt;
      } else {
        console.error('Invalid salt response structure:', saltResponseData);
        throw new Error('Invalid salt response from server');
      }
      const loginRequestUrl = `${getApiUrl()}/auth/auth/login`;
      
      const loginRequestBody = JSON.stringify({
        email: formData.email,
        password: formData.password,
        clientHash: false
      });
      
      const loginResponse = await fetch(loginRequestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: loginRequestBody
      });

      const loginData = await loginResponse.json();

      if (!loginResponse.ok) {
        console.error('Login failed:', loginData);
        throw new Error(loginData.message || 'Invalid email or password');
      }

      if (loginData.data && loginData.data.user && loginData.data.user.id) {
        localStorage.setItem('userId', loginData.data.user.id);
      }

      const jwt =
        loginData?.data?.data?.token ||
        loginData?.data?.token ||
        loginData?.token;
      if (jwt) {
        localStorage.setItem('token', jwt);
      }

      // Store partnerInfo from login response
      if (loginData.data && loginData.data.partnerInfo) {
        localStorage.setItem('partnerInfo', JSON.stringify(loginData.data.partnerInfo));
      }

      // Since this is a partner signin page, assume the user is a partner
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('isPartner', 'true');
      localStorage.setItem('partnerStatus', loginData.data?.partnerStatus || 'pending');
      localStorage.setItem('userEmail', formData.email);
      localStorage.setItem('userRole', loginData.data.user.role);
      // Check if user is admin
      if (loginData.data.user.role === 'admin') {
        navigate('/partner-admin');
      } else {
        navigate('/partner-dashboard');
      }
      
      setFormData({ email: '', password: '' });
    } catch (error) {      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error: Unable to connect to the server. Please check your internet connection.');
      } else if (error.message.includes('Salt request failed')) {
        setError(`Server error: ${error.message}`);
      } else if (error.message.includes('Invalid salt response')) {
        setError('Server configuration error. Please contact support.');
      } else {
        setError(error.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${getApiUrl()}/auth/auth/reset-password/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail })
      });

      if (!response.ok) {
        throw new Error('Password reset request failed');
      }

      alert('If a partner account exists with this email, you will receive password reset instructions.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      setError('Failed to process password reset request');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#f8fafc] to-[#e2e8f0] py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        {!showForgotPassword ? (
          <>
            <CardHeader className="text-center">
              <div className="text-4xl mb-4">🤝</div>
              <h2 className="text-2xl font-bold text-[#1a4a1a]">Welcome Back, Partner</h2>
              <p className="text-[#1a4a1a]/80">Sign in to access your partner dashboard</p>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 text-sm text-red-500 bg-red-50 rounded-md">
                  {error}
                </div>
              )}

              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    className="w-full"
                  />
                </div>

                <div>
                  <Input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 font-semibold py-3 rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                variant="link"
                onClick={() => setShowForgotPassword(true)}
                disabled={isLoading}
                className="text-emerald-600 hover:text-emerald-700"
              >
                Forgot Password?
              </Button>
              <p className="text-center text-[#1a4a1a]/70">
                Don't have a partner account?{' '}
                <Link to="/partner-signup" className="text-emerald-600 hover:underline font-semibold">
                  Sign up to launch Islamic financing
                </Link>
              </p>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader className="text-center">
              <h2 className="text-2xl font-bold text-[#1a4a1a]">Reset Password</h2>
              <p className="text-[#1a4a1a]/80">Enter your email to receive reset instructions</p>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 text-sm text-red-500 bg-red-50 rounded-md">
                  {error}
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 font-semibold py-3 rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </CardContent>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                disabled={isLoading}
                className="w-full"
              >
                Back to Sign In
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default PartnerSignIn; 